import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Field, getFormValues, reduxForm, reset, change,
} from 'redux-form';
import { t } from 'i18next';
import Row from '../ReactComponent/Row';
import Column from '../ReactComponent/Column';
import {
  isEmail, isPhone, isRequired, maxChar50, maxChar255,
} from '../../utils/validator';
import {
  ButtonContainer, FieldContainer,
  HeaderContainer,
  ReferenceNumberContainer,
  ReferenceNumberSummary,
  FieldLabel,
  FeedbackForm,
  Title,
  SubTitle,
} from './css';
import ResetOrange from '../../assets/icons/ResetOrange.svg';
import { phoneNumber } from '../../utils/mask';
import ResetFormModal from './ResetFormModal';
import SuccessModal from '../ReactComponent/SuccessModal';
import postFeedback from '../../services/feedbackService';
import { getUserById } from '../../services/users';
import TextInput from '../../lib/ui/form_elements/TextInput';
import TextArea from '../../lib/ui/form_elements/TextArea';
import Dropdown from '../../lib/ui/form_elements/Dropdown';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../lib/ui/buttons/SecondaryButton';

const formName = 'SubmitFeedbackForm';
const SubmitFeedbackFormFields = {
  fullName: 'fullName',
  companyName: 'companyName',
  email: 'email',
  phoneNumber: 'phoneNumber',
  referenceNumber: 'referenceNumber',
  feedbackMessage: 'feedbackMessage',
  reasonForContacting: 'reasonForContacting',
};

class SubmitFeedback extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      resetFormModalIsOpen: false,
      successModalIsOpen: false,
    };
  }

  componentDidMount() {
    const {
      isAuthenticated, dispatch, division, userId,
    } = this.props;

    if (isAuthenticated) {
      getUserById(division, userId)
        .then((response) => {
          if (response.status === 200) {
            dispatch(change(formName, SubmitFeedbackFormFields.fullName, response.data.profileDetails.fullName));
            dispatch(change(formName, SubmitFeedbackFormFields.email, response.data.userEmail));
          }
        });
    }
  }

  setResetFormModalIsOpen = (isOpen) => {
    this.setState({ resetFormModalIsOpen: isOpen });
  };

  setSuccessModalIsOpen = (isOpen) => {
    this.setState({ successModalIsOpen: isOpen });
  };

  resetForm = () => {
    const { dispatch } = this.props;
    dispatch(reset(formName));
  };

  handleSubmit = () => {
    const { formData } = this.props;
    postFeedback(formData)
      .then((res) => {
        if (res.status === 200) {
          this.setSuccessModalIsOpen(true);
        }
      });
  };

  renderHeader = () => {
    const { fields } = this.props;
    return (
      <HeaderContainer>
        <Column $desktop={8}>
          <Title>{fields.headerTitle.value}</Title>
          <SubTitle>{fields.headerSummary.value}</SubTitle>
        </Column>
      </HeaderContainer>
    );
  };

  renderForm = ({ isFormValid }) => {
    const { fields } = this.props;
    return (
      <form>
        <FeedbackForm>
          <Row>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.fullName}
                type="text"
                component={TextInput}
                label={t('FullName')}
                required
                validate={[isRequired, maxChar50]}
              />
            </FieldContainer>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.companyName}
                type="text"
                component={TextInput}
                label={t('CompanyName')}
                validate={[maxChar50]}
              />
            </FieldContainer>
          </Row>

          <Row>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.email}
                type="text"
                component={TextInput}
                label={t('Email')}
                required
                validate={[isRequired, isEmail, maxChar50]}
              />
            </FieldContainer>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.phoneNumber}
                type="text"
                component={TextInput}
                label={t('PhoneNumber')}
                required
                validate={[isRequired, isPhone]}
                normalize={phoneNumber}
              />
            </FieldContainer>
          </Row>

          <ReferenceNumberContainer>
            <FieldContainer $desktop={6}>
              <ReferenceNumberSummary field={fields.referenceNumberSummary} />
            </FieldContainer>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.referenceNumber}
                type="text"
                component={TextInput}
                label={t('ReferenceNumber')}
                validate={[maxChar50]}
              />
            </FieldContainer>
          </ReferenceNumberContainer>
          <Row>
            <FieldContainer $desktop={6}>
              <Field
                name={SubmitFeedbackFormFields.reasonForContacting}
                type="dropdown"
                component={Dropdown}
                label={t('ReasonForContacting')}
                options={[
                  { value: t('SharePositive') },
                  { value: t('SuggestImprovements') },
                  { value: t('QuestionOrAssistance') },
                ]}
                required
                validate={[isRequired]}
              />
            </FieldContainer>
          </Row>
          <Row>
            <FieldContainer $desktop={6}>
              <FieldLabel>
                {t('FeedbackMessage')}
              </FieldLabel>
              <Field
                name={SubmitFeedbackFormFields.feedbackMessage}
                type="textarea"
                component={TextArea}
                required
                hideCounter
                validate={[isRequired, maxChar255]}
              />
            </FieldContainer>
          </Row>
        </FeedbackForm>

        <ButtonContainer>
          <SecondaryButton
            type="button"
            showArrow={false}
            iconSrc={ResetOrange}
            onClick={() => this.setResetFormModalIsOpen(true)}
          >
            {t('Reset')}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            showArrow
            disabled={!isFormValid}
            onClick={() => this.handleSubmit()}
          >
            {t('Submit')}
          </PrimaryButton>
        </ButtonContainer>

      </form>
    );
  };

  render() {
    const { fields, pristine, invalid } = this.props;
    const { resetFormModalIsOpen, successModalIsOpen } = this.state;
    return (
      <div>
        <ResetFormModal
          isOpen={resetFormModalIsOpen}
          onClose={() => this.setResetFormModalIsOpen(false)}
          onReset={() => this.resetForm()}
          parentFields={fields}
        />
        <SuccessModal
          isOpen={successModalIsOpen}
          onClose={() => { this.resetForm(); this.setSuccessModalIsOpen(false); }}
          parentFields={fields}
        />
        {this.renderHeader()}
        {this.renderForm({ isFormValid: !(pristine || invalid) })}
      </div>
    );
  }
}

SubmitFeedback.propTypes = {
  fields: PropTypes.shape({
    headerTitle: PropTypes.shape({
      value: PropTypes.string,
    }),
    headerSummary: PropTypes.shape({
      value: PropTypes.string,
    }),
    referenceNumberSummary: PropTypes.object,
  }),
  formData: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  division: PropTypes.string,
  userId: PropTypes.string,
  dispatch: PropTypes.func,
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
};

SubmitFeedback = reduxForm({
  form: formName,
})(SubmitFeedback);

const mapStateToProps = (state) => ({
  formData: getFormValues(formName)(state),
  isAuthenticated: state.profile.isAuthenticated,
  division: state.profile.division,
  userId: state.profile.userId,
});

export default connect(mapStateToProps, {})(SubmitFeedback);
